import * as React from 'react';
import { Text, UnorderedList, ListItem } from '@chakra-ui/react';

import {
  UploadActions,
  UploadButtonSection,
  UploadComplete as UploadCompleteSection,
  Modal,
  AcknowledgeBox,
  RequirementsSummary
} from 'shared-components';

interface IProps {
  complete: boolean;
  fileNames: string[];
  onClose: () => void;
  onContinue: () => void;
  onUpload: () => void;
  docKey: string;
  infoOnly: boolean;
  docStatus: string;
  onAcknowledgeToggle: (status: string) => void;
}

const SecondaryBankStatementModal: React.FC<IProps> = (props: IProps) => {
  const { infoOnly, docStatus, docKey } = props;

  const handleOnClose = () => {
    if (docStatus === 'INFO_ONLY') {
      props.onAcknowledgeToggle('NOT_ACKNOWLEDGED');
    }
    props.onClose();
  };

  const toggleAcknowledge = (key: string, value: boolean) => {
    // prevent call to API if previous call not yet complete
    if ((value === true && docStatus !== 'ACKNOWLEDGED') || (value === false && docStatus === 'ACKNOWLEDGED')) {
      props.onAcknowledgeToggle(value === true ? 'ACKNOWLEDGED' : 'NOT_ACKNOWLEDGED');
    }
  };

  return (
    <Modal
      id={'secondaryBankStatement'}
      show={true}
      title={'Secondary Bank Statement Scan'}
      onClose={handleOnClose}
      hideFooter
    >
      {infoOnly ? (
        <>
          <Text>
            During your visa application you may be required to provide a letter from the bank that provides the term
            deposit account in your finance plan.
          </Text>

          <AcknowledgeBox
            id={docKey}
            blurb="Please confirm you have read and understand the above."
            isTicked={docStatus === 'ACKNOWLEDGED'}
            onChange={toggleAcknowledge}
          />

          <UploadActions
            label="Return to document upload"
            complete={docStatus === 'ACKNOWLEDGED'}
            onContinue={handleOnClose}
          />
        </>
      ) : (
        <>
          {props.complete ? (
            <UploadCompleteSection fileNames={props.fileNames} onUpload={props.onUpload} onContinue={props.onClose} />
          ) : (
            <>
              <Text>
                We require a statement from a second standard bank account (not a term deposit). Please provide a
                statement that demonstrates you have access to additional funds beyond your term deposit funds.
              </Text>

              <RequirementsSummary title="The statement must:">
                <UnorderedList>
                  <ListItem>Be dated less than 30 days ago</ListItem>
                  <ListItem>Show your name or the name of a parent / legal guardian</ListItem>
                </UnorderedList>
              </RequirementsSummary>

              <Text>
                Note: If you already added a secondary account to your finance plan you can upload the same statement
                here.
              </Text>

              <UploadButtonSection onUpload={props.onUpload}>
                Awaiting upload of <b>Secondary Bank Statement Scan</b>
              </UploadButtonSection>

              <UploadActions complete={props.complete} onContinue={props.onClose} />
            </>
          )}
        </>
      )}
    </Modal>
  );
};

export default SecondaryBankStatementModal;
